import { Injectable } from '@angular/core';
import { DynamicField } from '@cargos/sprintpay-models';
import { FacilityInformationType } from '@cargos/sprintpay-services';
import {
    BackgroundImages,
    BackgroundType,
    Config,
    FeaturesConfig,
    FeaturesConfigShipmentForm,
    FeaturesFlowType,
    FooterSection,
} from '../models/config';
import { PaymentCartTypeType } from '../models/payment-type';
import { PaidToFacilitiesId } from './firebase/models/organizations';

@Injectable({
    providedIn: 'root',
})
export class ConfigService {
    private config: Config = new Config();

    setConfig(config: Config): void {
        this.config = config;
    }

    getConfig(): Config {
        return this.config;
    }

    showBreadcrumbs(): boolean {
        return this.config.showBreadcrumbs || false;
    }

    getDefaultAWBPrefix(): string | null {
        return this.config.defaultAWBPrefix;
    }

    setChargesURL(chargesURL: string): void {
        this.config.chargesURL = chargesURL;
    }

    getChargesURL(): string {
        return this.config.chargesURL;
    }

    enableAccesories(enable: boolean): void {
        this.config.enableAccesories = enable;
    }

    areAccesoriesEnable(): boolean {
        return this.config.enableAccesories;
    }

    isAllowedAWBSubscription(): boolean {
        return this.config.allowAWBSubscription;
    }

    isAllowedSearchInvalidAWB(): boolean {
        return this.config.allowSearchInvalidAWB;
    }

    isAllowedContinueWithAWBNotFound(): boolean {
        return this.config.allowContinueWithAWBNotFound;
    }

    isAllowedMultipleAWBs(): boolean {
        return this.config.allowMultipleAWBsPayment;
    }

    forceToRequestDestination(): boolean {
        return this.config.forceToRequestDestination;
    }

    getMinimumAmountAllowed(): number {
        return this.config.minimumAmountAllowed;
    }

    isAllowedStoragePaymentsWithoutISC(): boolean {
        return this.config.allowStoragePaymentsWithoutISC;
    }

    isPaymentHistoryEnable(): boolean {
        return this.config.enablePaymentHistory;
    }

    isSearchByFlightNumberEnable(): boolean {
        return this.config.enableSearchByFlightNumber;
    }

    setAllowedPaymentsWithoutISCPaid(allow: boolean): void {
        this.config.allowPaymentsWithoutISCPaid = allow;
    }

    isAllowedPaymentsWithoutISCPaid(): boolean {
        return this.config.allowPaymentsWithoutISCPaid;
    }

    forceToRedirectionToCharges(): boolean {
        return this.config.forceToRedirectionToCharges;
    }

    getPaymentTypesListNoNeedISC(): string[] {
        return this.config.paymentTypesNoNeedISC;
    }

    isEnableTabsCharges(): boolean {
        return this.config.enableTabsCharges;
    }

    isEnableNomination(): boolean {
        return this.config.enableNomination;
    }

    getCreditCardAnnouncement(): string {
        return this.config.creditCardAnnouncement;
    }

    isCompanyPolicyRequired(): boolean {
        return this.config.acceptCompanyPolicyRequired;
    }

    getPaymentCartType(): PaymentCartTypeType {
        return this.config.paymentCartType;
    }

    isDisableChargeButtonWhenStatusDLV(): boolean {
        return this.config.disableChargeButtonWhenStatusDLV;
    }

    logicBaseOnFlightNumberAndFlightDate(): boolean {
        return this.config.logicBaseOnFlightNumberAndFlightDate;
    }

    isEnablePickupTimeOnPickupDate(): boolean {
        return this.config.enablePickupTimeOnPickupDate;
    }

    addFacilityCode(): boolean {
        return this.config.addFacilityCode;
    }

    isAGIWalletEnable(): boolean {
        return this.config.enableAGIWallet;
    }

    calculateQuantityByExtraItems(): boolean {
        return this.config.calculateQuantityOnExtraItems;
    }

    isenableMessageForAmountZero(): boolean {
        return this.config.enableMessageForAmountZero;
    }

    isPromoCodeEnable(): boolean {
        return this.config.enablePromoCode;
    }

    isEnableSearchButtonOutside(): boolean {
        return this.config.enableSearchButtonOutside;
    }

    isEnableRequestorApprovalFlow(): boolean {
        return this.config.enableRequestorApprovalFlow;
    }

    isEcheckEnableAsGuestPayment(): boolean {
        return this.config.enableEchecksAsGuestPayment;
    }

    isEnableOptimizationToCalculateStorage(): boolean {
        return this.config.optimizeCalculationStorage;
    }

    addFirmsCodeInExternalData(): boolean {
        return this.config.addFirmsCodeInExternalData;
    }

    collectCompanyInformationByPayment(): boolean {
        return this.config.collectCompanyInformationByPayment;
    }

    isEnableDynamicFields(): boolean {
        return this.config.enableDynamicFields;
    }

    isTransferCartEnable(): boolean {
        return this.config.enableTransferCart;
    }

    isEnableFAQPDF(): boolean {
        return this.config.enableFAQPDF;
    }

    /**
     * If set to null, there is no limit on the number of items allowed per cart
     * @returns {number | null} The maximum number of items allowed per cart or null if no limit
     */
    getMaxItemsPerCart(): number | null {
        return this.config.maxItemsPerCart;
    }

    isTermsOfUseExternal(): boolean {
        return this.config.areTermsOfUseExternal;
    }

    isAutoPaySubscriptionEnable(): boolean {
        return this.config.enableAutoPaySubscription;
    }

    getLoginSSOTarget(): string {
        return this.config.loginSSOTarget;
    }

    getMaxWidthHeader(): string {
        return this.config.maxWidthHeader;
    }

    isEnableChargeEdition(): boolean {
        return this.config.enableChargeEdition;
    }

    getSearchAWBLabel(): string {
        return this.config.searchAWBLabel;
    }

    getLabelAWB(): string {
        return this.config.labelAWB;
    }

    setLabelAWB(labelAWB: string): void {
        this.config.labelAWB = labelAWB;
    }

    getLabelHAWB(): string {
        return this.config.labelHAWB;
    }

    setLabelHAWB(labelHAWB: string): void {
        this.config.labelHAWB = labelHAWB;
    }
    getPropertyByName<K extends keyof Config>(propertyName: K): Config[K] | undefined | null {
        return this.config[propertyName];
    }

    isISCZero(): boolean {
        return this.config.isISCZero;
    }

    isAllowedPayWithoutShipmentInformation(): boolean {
        return this.config.allowPaymentsWithoutShipmentInformation;
    }

    setDarkMode(enable: boolean): void {
        this.config.enableDarkMode = enable || false;
    }

    isDarkModeEnable(): boolean {
        return this.config.enableDarkMode || false;
    }

    getDynamicFields(): DynamicField[] {
        return this.config.shipmentForm?.dynamicFields || [];
    }

    showFooter(): boolean {
        return this.config.showFooter || false;
    }

    getAllowMultiplePayments(): boolean {
        return this.config.allowMultiplePayments || false;
    }

    getFooterLogoHeight(): string {
        return this.config.footerLogoHeight || 'auto';
    }

    getFooterLogoWidth(): string {
        return this.config.footerLogoWidth || 'auto';
    }

    getFooterCompanyLogo(): string {
        return this.config.footerCompanyLogo || '';
    }

    getFooterSection(): FooterSection[] {
        return this.config.footerSections || [];
    }

    getAllowedDestinations(): string[] {
        return this.config.allowedDestinations;
    }

    getSupportedDestinations(): string {
        return this.config.supportedDestinations || '';
    }

    isEnableSearchFlow(): boolean {
        return this.config.allowSearchForm || false;
    }

    isEnableShipmentFlow(): boolean {
        return this.config.allowShipmentForm || false;
    }

    isEnableShadow(): boolean {
        return !this.config.removeShadow || false;
    }

    getBackgroundType(): BackgroundType {
        return this.config.backgroundType || '';
    }

    getBackgroundImages(): BackgroundImages[] {
        return this.config.backgroundImages || [];
    }

    isEnableFlightDateInRequest(): boolean {
        return this.config.enableFlightDateInRequest || false;
    }

    getBackgroundImageTitle(): string {
        return this.config.backgroundImageTitle || '';
    }

    getCenterBackgroundImages(): boolean {
        return this.config.centerBackgroundImages || false;
    }

    showReceiptSent(): boolean {
        return !!this.config?.showReceiptSent;
    }

    getFeaturesConfig(): FeaturesConfig | undefined {
        return this.config.features;
    }

    getHeaderConfig(): FeaturesConfig['headerConfig'] | undefined {
        return this.config.features?.headerConfig;
    }

    getSearchFormConfig(): FeaturesConfig[FeaturesFlowType.SEARCH_FORM] | null | undefined {
        return this.config.features?.searchForm;
    }

    getEnableThresholdReceiptAPISwitch(): boolean {
        return this.config.enableThresholdReceiptAPISwitch;
    }

    isCreditCardVerificationEnabled(): boolean {
        return this.config.enableCreditCardVerification;
    }

    setPaidToMultipleFacilitiesEnable(enabled: boolean): void {
        this.config.paidToMultipleFacilitiesEnable = enabled;
    }

    isPaidToMultipleFacilitiesEnable(): boolean {
        return this.config.paidToMultipleFacilitiesEnable;
    }

    setPaidToFacilitiesId(facilities: PaidToFacilitiesId[]): void {
        this.config.paidToFacilitiesId = facilities || [];
    }

    getPaidToFacilitiesId(): PaidToFacilitiesId[] {
        return this.config.paidToFacilitiesId || [];
    }

    isCustomerReferenceRequired(): boolean {
        return this.config.customerReferenceRequired ?? true;
    }

    getbackgroundImagesConfig(): BackgroundImages[] {
        if (this.isEnableSearchFlow()) {
            return this.config.searchForm?.backgroundImages || [];
        }
        if (this.isEnableShipmentFlow()) {
            return this.config.shipmentForm?.backgroundImages || [];
        }
        return [];
    }

    sortArrayByproperty<T>(array: T[], property: string, order: 'asc' | 'desc' = 'asc'): T[] {
        if (!array || !array.length) {
            return [];
        }
        return array.slice().sort((a, b) => {
            if (order === 'asc') {
                return a[property] - b[property];
            }
            return b[property] - a[property];
        });
    }

    getShipmentOrderedById(): FeaturesConfigShipmentForm | undefined {
        const shipmentForm = this.config.features?.shipmentForm;
        const dynamic = 'dynamicFields';
        if (shipmentForm && shipmentForm[dynamic] && shipmentForm[dynamic].length > 0) {
            const sortedDynamicFields = this.sortArrayByproperty<DynamicField>(shipmentForm.dynamicFields, 'id');
            const composedObject = {
                [dynamic]: sortedDynamicFields,
            };
            return composedObject;
        }
        return undefined;
    }

    mapShipmentValues(formValues: Record<string, unknown>): Record<string, string> {
        const mapped: Record<string, string> = {};
        if (!formValues) return mapped;

        const dynamicFields: DynamicField[] = this.config.features?.shipmentForm?.dynamicFields || [];
        dynamicFields.forEach((meta) => {
            if (meta.isValueMappingEnabled && meta.mappedFieldName && meta.name) {
                const val = formValues[meta.name];
                if (val !== undefined && val !== null && val !== '') {
                    mapped[String(meta.mappedFieldName)] = String(val);
                }
            }
        });

        return mapped;
    }

    getShipmentFormConfig(): FeaturesConfigShipmentForm | null | undefined {
        return this.config.features?.shipmentForm;
    }

    /**
     * Returns true when shipmentForm dynamic fields define a mapping to fieldName.
     */
    isFieldMapped(fieldName: string): boolean {
        const dynamicFields: DynamicField[] = this.config.features?.shipmentForm?.dynamicFields || [];
        return dynamicFields.some((meta: DynamicField) => {
            return Boolean(meta.isValueMappingEnabled && meta.mappedFieldName === fieldName);
        });
    }

    /**
     * Returns true when shipmentForm dynamic fields define a mapping to `notificationEmail`.
     * Convenience typed helper used by payment request flows.
     */
    isNotificationEmailMapped(): boolean {
        return this.isFieldMapped('notificationEmail');
    }

    getFeaturesFlowType(): FeaturesFlowType | undefined {
        return this.config.features?.typeFlow;
    }

    isEnabledFacilityInformation(): boolean {
        return !!this.config.showFacilityInformation;
    }

    getFacilityInformation(): FacilityInformationType[] {
        return this.config.facilityInformation || [];
    }
}
